const MAX_NEED_TO_KNOW_ITEMS = 5;
const MAX_DETAILS_PER_CATEGORY = 2;

const normalizeText = (value = "") =>
  String(value || "").replace(/\s+/g, " ").trim();

const getChapterHeadline = (chapter = {}) => {
  const handbookEntry = chapter.handbook?.groups
    ?.flatMap((group) => group.entries || [])
    ?.find((entry) => normalizeText(entry.headline));
  if (handbookEntry) return normalizeText(handbookEntry.headline);

  const briefingItem = chapter.briefing?.items
    ?.find((item) => normalizeText(item.text));
  return normalizeText(briefingItem?.text);
};

const buildChapterItem = ({ id, category, label, tone, chapter }) => {
  const text = getChapterHeadline(chapter);
  return text ? { id, category, label, tone, text } : null;
};

const mergeCategoryItems = (items = []) => {
  const [first] = items;
  const seen = new Set();
  const details = items
    .map((item) => normalizeText(item.text))
    .filter((text) => {
      const key = text.toLowerCase().replace(/[.!?]+$/, "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_DETAILS_PER_CATEGORY);

  return { ...first, text: details.join(" • ") };
};

export const buildProfileLandingNeedToKnowItems = ({
  existingItems = [],
  communicationChapter,
  whatHelpsChapter,
  watchForChapter,
  safetyChapter,
} = {}) => {
  const existingById = new Map(existingItems.map((item) => [item.id, item]));
  const candidates = [
    existingById.get("allergy")
      ? { ...existingById.get("allergy"), category: "allergy" }
      : null,
    existingById.get("medication")
      ? { ...existingById.get("medication"), category: "medication" }
      : null,
    existingById.get("safety")
      ? { ...existingById.get("safety"), category: "safety" }
      : null,
    buildChapterItem({
      id: "profile_safety",
      category: "safety",
      label: "Safety",
      tone: "emergency",
      chapter: safetyChapter,
    }),
    existingById.get("emergency_contact")
      ? { ...existingById.get("emergency_contact"), category: "emergency_contact" }
      : null,
    existingById.get("communication")
      ? { ...existingById.get("communication"), category: "communication" }
      : null,
    buildChapterItem({
      id: "profile_communication",
      category: "communication",
      label: "Communication",
      tone: "support",
      chapter: communicationChapter,
    }),
    buildChapterItem({
      id: "profile_watch_for",
      category: "watch_for",
      label: "Watch For",
      tone: "critical",
      chapter: watchForChapter,
    }),
    buildChapterItem({
      id: "profile_what_helps",
      category: "what_helps",
      label: "What Helps",
      tone: "support",
      chapter: whatHelpsChapter,
    }),
  ].filter(Boolean);

  const itemsByCategory = new Map();
  candidates.forEach((item) => {
    const categoryItems = itemsByCategory.get(item.category) || [];
    categoryItems.push(item);
    itemsByCategory.set(item.category, categoryItems);
  });

  return [...itemsByCategory.values()]
    .map(mergeCategoryItems)
    .filter((item) => item.text)
    .slice(0, MAX_NEED_TO_KNOW_ITEMS);
};
